import { createListenerMiddleware } from '@reduxjs/toolkit';
import Analytics from '../analytics';
import { setApiAuthToken, removeApiAuthToken } from '../api/api-helper';
import {
  postBookingBookThunk,
  postBookingDetailsThunk,
  postGoogleBookingDetailsThunk,
  setBookingFlowState,
} from './bookingSlice';
import bookingFlowSideEffects from './helpers/bookingFlowSideEffects';
import { setSearchParams } from '../utils/searchParamUtils';
import {
  ALL_DAY, CORE_DATE_PARAM, CORE_SEATS_PARAM, CORE_TIME_PARAM,
} from '../constants/resyCore';

import {
  MODAL_MODES,
  getMultipleLists,
  getMultipleListsByVenue,
  editListThunk,
  resetModalErrorState,
  setMultiStepModalMode,
  setMultiStepModalOpenState,
  setMultiStepModalVenueId,
  setMultiStepModalVenueContext,
} from './favoriteSlice';

import { checkIfWidget, Logger } from '../utils';
import { getLocationPath, getLocationUrl, getSearchParamsAsObject } from '../utils/locationUtils';

const $locationReadOnlyShim = {
  path: getLocationPath,
  url: getLocationUrl,
  search: getSearchParamsAsObject,
};

const listenerMiddleware = createListenerMiddleware();

listenerMiddleware.startListening({
  actionCreator: setBookingFlowState,
  effect: (action, { getState }) => {
    // you need to pass the token along if this is navigating to confirmation
    // so you can set it in search
    bookingFlowSideEffects(getState());
  },
});

listenerMiddleware.startListening({
  actionCreator: postBookingBookThunk.fulfilled,
  effect: () => {
    /**
      * resy.com won't know that the reservation was cancelled without a refresh,
      * so this is how we will tell it
    */
    if (window.opener) {
      window.opener.postMessage('onResyReservationsSync', '*');
    } else {
      window.parent.postMessage('onResyReservationsSync', '*');
    }
  },
});

listenerMiddleware.startListening({
  actionCreator: editListThunk.fulfilled,
  effect: (action, listenerApi) => {
    listenerApi.dispatch(getMultipleLists());
  },
});

listenerMiddleware.startListening({
  actionCreator: setMultiStepModalMode,
  effect: (action, listenerApi) => {
    if (action.payload === MODAL_MODES.ADD_TO_LIST) {
      const state = listenerApi.getState();
      const venueId = state.favorite.multiStepModalVenueId;
      listenerApi.dispatch(getMultipleListsByVenue(venueId));
    }
  },
});

listenerMiddleware.startListening({
  actionCreator: setMultiStepModalOpenState,
  effect: (action, listenerApi) => {
    if (action.payload === false) {
      listenerApi.dispatch(setMultiStepModalVenueId());
      listenerApi.dispatch(setMultiStepModalVenueContext());
    }
  },
});

listenerMiddleware
  .startListening({
    type: 'resyCore/setSelectedCoreISODate',
    effect: (action, { getState }) => {
      const { resyCore, resyRouter } = getState();
      const { metadataPageTitle, metadataTitle, pageName } = resyRouter;
      const $rootScopeMetadataShim = {
        title: metadataPageTitle || metadataTitle,
        pageName,
      };
      if (resyCore.didCoreISODateUpdate) {
        setSearchParams(CORE_DATE_PARAM, resyCore.selectedCoreISODate);
        if (checkIfWidget()) {
          Analytics().page($locationReadOnlyShim, document.referrer, $rootScopeMetadataShim);
        }
      }
    },
  });

listenerMiddleware
  .startListening({
    type: 'resyCore/setSelectedCoreSeats',
    effect: (action, { getState }) => {
      const { resyCore, resyRouter } = getState();
      const { metadataPageTitle, metadataTitle, pageName } = resyRouter;
      const $rootScopeMetadataShim = {
        title: metadataPageTitle || metadataTitle,
        pageName,
      };
      if (resyCore.didCoreSeatsUpdate) {
        setSearchParams(CORE_SEATS_PARAM, resyCore.selectedCoreSeats);
        if (checkIfWidget()) {
          Analytics().page($locationReadOnlyShim, document.referrer, $rootScopeMetadataShim);
        }
      }
    },
  });

listenerMiddleware
  .startListening({
    type: 'resyCore/setSelectedCoreTime',
    effect: (action, { getState }) => {
      const { resyCore } = getState();
      if (resyCore.didCoreTimeUpdate) {
        const selectedCoreTime = resyCore.selectedCoreTime === ALL_DAY ? null : resyCore.selectedCoreTime;
        setSearchParams(CORE_TIME_PARAM, selectedCoreTime);
      }
    },
  });

listenerMiddleware
  .startListening({
    type: 'authToken/setAuthToken',
    effect: (action) => {
      const token = action?.payload;
      if (token) {
        setApiAuthToken(token);
      }
    },
  });

listenerMiddleware.startListening({
  type: 'authToken/removeAuthToken',
  effect: () => {
    removeApiAuthToken();
  },
});

listenerMiddleware
  .startListening({
    type: 'favorite/setErrorModalOpenState',
    effect: (action, listenerApi) => {
      if (action.payload === false) {
        listenerApi.dispatch(resetModalErrorState());
      }
    },
  });

listenerMiddleware
  .startListening({
    type: 'user/setUserData',
    effect: (action) => {
      const userData = action.payload;
      Analytics().setUser(userData);
    },
  });

listenerMiddleware
  .startListening({
    actionCreator: postBookingDetailsThunk.rejected,
    effect: (action) => {
      Logger.error(action.payload);
    },
  });

listenerMiddleware
  .startListening({
    actionCreator: postGoogleBookingDetailsThunk.rejected,
    effect: (action) => {
      Logger.error(action.payload);
    },
  });

listenerMiddleware
  .startListening({
    actionCreator: postBookingBookThunk.rejected,
    effect: (action) => {
      Logger.error(action.payload);
    },
  });

export default listenerMiddleware;
